import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Menu, X, Clock, Tag, ChevronRight,
  Facebook, Instagram, Youtube, MessageCircle, Search
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Anim = ({ children, className = "", delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}>{children}</motion.div>
  );
};

const BlogHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const navItems = [
    { label: "Home", href: "/" },
    { label: "Fertility Nutrition", href: "/fertility-nutrition" },
    { label: "Blog", href: "/blog" },
    { label: "Programs", href: "/#programs" },
    { label: "Contact", href: "/#contact" }
  ];
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-bone/95 backdrop-blur-xl shadow-sm" : "bg-emerald_deep"}`} data-testid="blog-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-serif text-2xl tracking-tight">
            <span className={`font-semibold ${scrolled ? "text-emerald_deep" : "text-bone"}`}>Fitly</span>
            <span className={`font-light ml-1 ${scrolled ? "text-text_muted" : "text-bone/60"}`}>India</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map(item => (
              <a key={item.label} href={item.href} className={`hover:text-accent transition-colors text-sm tracking-wide ${scrolled ? "text-text_muted" : "text-bone/70"}`}>{item.label}</a>
            ))}
          </nav>
          <div className="hidden md:block">
            <a href="/consultation" className="bg-accent text-emerald_deep px-7 py-2.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all inline-flex items-center gap-2 rounded-full">
              Book Consultation <ArrowRight size={14} />
            </a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden ${scrolled ? "text-emerald_deep" : "text-bone"}`}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden py-6 bg-surface border border-border_custom rounded-xl mt-2 mb-4 shadow-lg">
            {navItems.map(item => (
              <a key={item.label} href={item.href} className="block w-full text-left px-6 py-3 text-text_muted hover:text-emerald_deep hover:bg-bone/50 text-sm tracking-wide">{item.label}</a>
            ))}
            <div className="px-6 pt-3">
              <a href="/consultation" className="block w-full bg-emerald_deep text-bone py-3 text-sm tracking-wide text-center rounded-full">Book Consultation</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const BlogFooter = () => (
  <footer className="bg-emerald_deep text-bone py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="font-serif text-3xl mb-4"><span className="text-accent">Fitly</span> India</div>
          <p className="text-bone/60 leading-relaxed max-w-sm">Evidence-based fertility nutrition articles and guides for your conception journey.</p>
          <div className="flex gap-4 mt-6">
            {[[Facebook, "https://www.facebook.com/294056110466070"], [Instagram, "https://www.instagram.com/fitly_india"], [Youtube, "https://www.youtube.com/fitly_india"]].map(([Icon, href]) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-bone/10 flex items-center justify-center hover:bg-accent/20 transition-colors"><Icon size={16} className="text-bone/70" /></a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {[["Home", "/"], ["Fertility Nutrition", "/fertility-nutrition"], ["Female Fertility", "/female-fertility-nutrition"], ["Male Fertility", "/male-fertility-nutrition"], ["Consultation", "/consultation"]].map(([label, href]) => (
              <li key={label}><a href={href} className="text-bone/60 hover:text-accent transition-colors text-sm">{label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-bone/60">
            <li><a href="tel:+919108818369" className="hover:text-accent transition-colors">+91 91088 18369</a></li>
            <li><a href="mailto:fitlyindiahealth@gmail.com" className="hover:text-accent transition-colors">fitlyindiahealth@gmail.com</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-bone/10 pt-8 text-center text-bone/40 text-xs">&copy; {new Date().getFullYear()} Fitly India. All rights reserved.</div>
    </div>
  </footer>
);

const PostCard = ({ post, idx }) => {
  const readTime = Math.max(3, Math.ceil((post.excerpt?.length || 100) / 40));
  return (
    <Anim delay={idx * 0.08}>
      <Link to={`/blog/${post.slug}`} className="group block h-full" data-testid={`blog-card-${post.slug}`}>
        <article className="bg-surface border border-border_custom rounded-xl overflow-hidden hover:shadow-lg transition-all h-full flex flex-col">
          {post.featured_image && (
            <div className="aspect-[16/9] overflow-hidden">
              <img src={post.featured_image} alt={post.title} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
          )}
          <div className="p-6 flex flex-col flex-1">
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {post.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="text-[10px] tracking-wider uppercase text-accent bg-accent/10 px-2.5 py-1 rounded-full font-medium">{tag}</span>
                ))}
              </div>
            )}
            <h2 className="font-serif text-lg md:text-xl text-emerald_deep mb-3 group-hover:text-accent transition-colors leading-snug">{post.title}</h2>
            <p className="text-sm text-text_muted leading-relaxed mb-4 flex-1">{post.excerpt}</p>
            <div className="flex items-center justify-between text-xs text-text_muted/70 pt-3 border-t border-border_custom">
              <span className="flex items-center gap-1.5"><Clock size={12} /> {readTime} min read</span>
              <span className="flex items-center gap-1 text-accent font-medium group-hover:gap-2 transition-all">Read More <ChevronRight size={12} /></span>
            </div>
          </div>
        </article>
      </Link>
    </Anim>
  );
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPosts = async () => {
      try {
        const url = selectedTag ? `${API}/blog?tag=${encodeURIComponent(selectedTag)}` : `${API}/blog`;
        const res = await axios.get(url);
        setPosts(res.data.posts || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [selectedTag]);

  const allTags = [...new Set(posts.flatMap(p => p.tags || []))];

  const filtered = searchQuery
    ? posts.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
    : posts;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Fitly India Blog",
    "description": "Evidence-based fertility nutrition articles covering PCOS, AMH, egg quality, male fertility and IVF preparation.",
    "url": "https://fitlyindia.org/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Fitly India",
      "url": "https://fitlyindia.org"
    }
  };

  return (
    <div className="bg-bone text-text_main" data-testid="blog-page">
      <Helmet>
        <title>Fertility Nutrition Blog | PCOS, AMH, Egg Quality Articles | Fitly India</title>
        <meta name="description" content="Evidence-based fertility nutrition articles from Fitly India. Expert guides on PCOS diet, AMH, egg quality, male fertility, IVF preparation and preconception nutrition." />
        <link rel="canonical" href="https://fitlyindia.org/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitlyindia.org/blog" />
        <meta property="og:title" content="Fertility Nutrition Blog | Fitly India" />
        <meta property="og:description" content="Evidence-based fertility nutrition articles on PCOS, AMH, egg quality and more from Fitly India." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <BlogHeader />

      <main>
        {/* Hero */}
        <section className="bg-emerald_deep pt-28 pb-16 md:pb-20" data-testid="blog-hero">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Anim>
              <nav aria-label="Breadcrumb" className="mb-6">
                <ol className="flex items-center gap-2 text-xs text-bone/40">
                  <li><a href="/" className="hover:text-accent transition-colors">Home</a></li><li>/</li>
                  <li className="text-accent">Blog</li>
                </ol>
              </nav>
            </Anim>
            <Anim delay={0.1}>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-bone mb-4">
                Fertility Nutrition <span className="italic font-light text-accent">Blog</span>
              </h1>
            </Anim>
            <Anim delay={0.2}>
              <p className="text-lg text-bone/70 max-w-2xl mb-8">
                Evidence-based articles on fertility nutrition, hormonal health and preconception wellness from Fitly India's expert team.
              </p>
            </Anim>
            <Anim delay={0.3}>
              <div className="relative max-w-md">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-bone/40" />
                <input
                  type="text" placeholder="Search articles..." value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  data-testid="blog-search"
                  className="w-full pl-11 pr-4 py-3 bg-bone/10 border border-bone/20 rounded-full text-bone placeholder:text-bone/40 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
              </div>
            </Anim>
          </div>
        </section>

        {/* Tags filter */}
        {allTags.length > 0 && (
          <section className="bg-bone border-b border-border_custom py-4" data-testid="blog-tags">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <Tag size={14} className="text-text_muted flex-shrink-0" />
                <button onClick={() => setSelectedTag(null)} data-testid="tag-all"
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${!selectedTag ? "bg-emerald_deep text-bone" : "bg-surface border border-border_custom text-text_muted hover:text-emerald_deep"}`}>
                  All
                </button>
                {allTags.map(tag => (
                  <button key={tag} onClick={() => setSelectedTag(tag)} data-testid={`tag-${tag.toLowerCase().replace(/\s/g, '-')}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${selectedTag === tag ? "bg-emerald_deep text-bone" : "bg-surface border border-border_custom text-text_muted hover:text-emerald_deep"}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Posts grid */}
        <section className="py-16 md:py-20" data-testid="blog-posts-grid">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-20 text-text_muted">Loading articles...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-text_muted text-lg">No articles found.</p>
                {searchQuery && <p className="text-sm text-text_muted/70 mt-2">Try a different search term.</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((post, idx) => (
                  <PostCard key={post.id || post.slug} post={post} idx={idx} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-emerald_deep" data-testid="blog-cta">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <Anim>
              <h2 className="font-serif text-3xl sm:text-4xl text-bone mb-4">
                Ready for <span className="italic font-light text-accent">Personalised Guidance?</span>
              </h2>
              <p className="text-bone/70 mb-8">Our articles provide general information. For nutrition tailored to your specific condition and goals, speak with our fertility nutrition team.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/consultation" data-testid="blog-cta-consult" className="inline-flex items-center justify-center gap-2 bg-accent text-emerald_deep px-10 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book Consultation <ArrowRight size={16} />
                </a>
                <a href="https://wa.me/919108818369?text=Hi!%20I%20read%20your%20blog%20and%20would%20like%20to%20learn%20more." target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-bone/20 text-bone px-8 py-3.5 text-sm tracking-wide hover:bg-bone/10 transition-all rounded-full">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </Anim>
          </div>
        </section>
      </main>

      <BlogFooter />
    </div>
  );
}
